'use client';

import { useMemo, useRef, useSyncExternalStore } from 'react';
import { computeRelativeTime, type RelativeTimeComputation, toTimestamp } from '@/lib/utils/time';
import { clearWindowTimeout, setWindowTimeout } from '@/lib/utils/window-timers';

export interface UseRelativeTimeOptions extends Pick<Intl.RelativeTimeFormatOptions, 'style' | 'numeric'> {
  /**
   * Allows callers to suspend updates (e.g., when the element is off-screen).
   */
  active?: boolean | undefined;
  /**
   * Mirrors the server time used during SSR. Pass Date.now() from the server component
   * to avoid hydration mismatches when you need deterministic first render.
   */
  initialNowMs?: number | undefined;
  locale?: string | undefined;
  /**
   * Lower bound for timer cadence. Defaults to 1 second to avoid tight loops.
   */
  minimumUpdateIntervalMs?: number | undefined;
}

interface UseRelativeTimeResult {
  isValid: boolean;
  label: string;
  result: RelativeTimeComputation | null;
}

const UNKNOWN_TIME_LABEL = 'unknown time';
const JUST_NOW_LABEL = 'just now';
const MAX_TIMEOUT_MS = 2_147_483_647; // setTimeout upper bound on 32-bit signed integer platforms

interface RelativeTimeTickerState {
  active: boolean;
  nextDelayMs: number;
}

interface TickerStateReference {
  current: RelativeTimeTickerState;
}

const relativeTimeTicker = (() => {
  const subscriptions = new Map<TickerStateReference, () => void>();
  let timerId: number | null = null;
  let nowMs = Date.now();

  const clearTimer = () => {
    if (timerId !== null) {
      clearWindowTimeout(timerId);
      timerId = null;
    }
  };

  const tick = () => {
    nowMs = Date.now();
    // Snapshot listeners to avoid issues if they unsubscribe while iterating.
    const listeners = [...subscriptions.values()];
    for (const listener of listeners) {
      listener();
    }
    schedule();
  };

  const schedule = () => {
    if (subscriptions.size === 0) {
      clearTimer();
      return;
    }

    let delay = MAX_TIMEOUT_MS;
    let hasActive = false;
    for (const reference of subscriptions.keys()) {
      const { active, nextDelayMs } = reference.current;
      if (!active) {
        continue;
      }
      hasActive = true;
      if (Number.isFinite(nextDelayMs)) {
        delay = Math.min(delay, Math.max(16, nextDelayMs));
      }
    }

    if (!hasActive) {
      clearTimer();
      return;
    }

    const clampedDelay = Math.min(delay, MAX_TIMEOUT_MS);
    if (timerId !== null) {
      clearWindowTimeout(timerId);
    }
    timerId = setWindowTimeout(tick, clampedDelay);
    if (timerId === null) {
      timerId = setWindowTimeout(tick, MAX_TIMEOUT_MS);
    }
  };

  return {
    subscribe: (reference: TickerStateReference, listener: () => void) => {
      subscriptions.set(reference, listener);
      schedule();
      return () => {
        subscriptions.delete(reference);
        if (subscriptions.size === 0) {
          clearTimer();
        } else {
          schedule();
        }
      };
    },
    update: (reference: TickerStateReference) => {
      if (!subscriptions.has(reference)) {
        return;
      }
      schedule();
    },
    getSnapshot: () => nowMs,
  };
})();

export function useRelativeTime(
  input: Date | string | number | null | undefined,
  {
    locale,
    style = 'long',
    numeric = 'auto',
    initialNowMs,
    active = true,
    minimumUpdateIntervalMs = 1000,
  }: UseRelativeTimeOptions = {}
): UseRelativeTimeResult {
  const targetMs = useMemo(() => toTimestamp(input), [input]);
  const initialNowReference = useRef(initialNowMs ?? Date.now());
  const tickerStateReference = useRef<RelativeTimeTickerState>({
    active: false,
    nextDelayMs: Number.POSITIVE_INFINITY,
  });

  const subscribe = useMemo(
    () => (listener: () => void) => relativeTimeTicker.subscribe(tickerStateReference, listener),
    []
  );

  const nowMs = useSyncExternalStore(subscribe, relativeTimeTicker.getSnapshot, () => initialNowReference.current);
  const effectiveNowMs = active ? nowMs : initialNowReference.current;

  const resolvedLocale = useMemo(() => {
    if (typeof locale === 'string' && locale.trim().length > 0) {
      return locale;
    }
    if (navigator !== undefined && typeof navigator.language === 'string' && navigator.language.length > 0) {
      return navigator.language;
    }
    return 'en-US';
  }, [locale]);

  const computation = useMemo(() => {
    if (targetMs === undefined || targetMs === null) {
      return null;
    }
    return computeRelativeTime(targetMs, {
      locale: resolvedLocale,
      style,
      numeric,
      nowMs: effectiveNowMs,
    });
  }, [targetMs, resolvedLocale, style, numeric, effectiveNowMs]);

  const hasComputation = computation !== null && computation !== undefined;

  let label: string;
  if (!hasComputation) {
    label = UNKNOWN_TIME_LABEL;
  } else if (computation.value === 0 && computation.unit === 'second') {
    label = JUST_NOW_LABEL;
  } else {
    label = computation.label;
  }

  tickerStateReference.current = {
    active: Boolean(active && targetMs !== undefined),
    nextDelayMs:
      hasComputation && active
        ? Math.max(minimumUpdateIntervalMs, computation.nextUpdateInMs)
        : Number.POSITIVE_INFINITY,
  };

  relativeTimeTicker.update(tickerStateReference);

  return {
    label,
    result: computation,
    isValid: targetMs !== undefined,
  };
}
